import { Component, type ReactNode } from 'react'
import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Ban, Home } from 'lucide-react'
import { Button, Card } from '@/components/ui'
import { isSupportSessionBlocked } from '@/lib/supportSessionErrors'

/**
 * EP-160 S3: "⛔ หน้านี้ไม่เปิดให้ทีม FlowRent ในเซสชันช่วยเหลือ", shown in place
 * of a page whose data the session's deny list refuses (403
 * support_session_blocked: the activity log, the closing package, statements,
 * account deletion and the other SupportSession::Blocked actions).
 */
export function SupportSessionBlockedState() {
  const { t } = useTranslation('common')
  return (
    <Card
      className="max-w-xl mx-auto"
      bodyClassName="items-center text-center gap-4"
      role="alert"
      data-testid="support-session-blocked"
    >
      <div className="w-14 h-14 rounded-full bg-error/10 flex items-center justify-center">
        <Ban className="w-7 h-7 text-error" aria-hidden="true" />
      </div>
      <h1 className="card-title">{t('support_session.blocked_title')}</h1>
      <p className="text-base-content/70">{t('support_session.blocked_body')}</p>
      <Button as={Link} to="/" variant="outline" leftIcon={<Home className="w-4 h-4" aria-hidden="true" />}>
        {t('support_session.blocked_home')}
      </Button>
    </Card>
  )
}

interface BoundaryProps {
  children: ReactNode
}

interface BoundaryState {
  error: unknown
}

/**
 * The page error boundary for support_session_blocked. queryClient.ts throws
 * a page query that failed with that code (throwOnError); this catches it and
 * shows SupportSessionBlockedState. Any other error is thrown on to the app's
 * Sentry boundary. MainLayout keys it by path, so navigating away resets it.
 */
export class SupportSessionBlockedBoundary extends Component<BoundaryProps, BoundaryState> {
  state: BoundaryState = { error: null }

  static getDerivedStateFromError(error: unknown): BoundaryState {
    return { error }
  }

  render() {
    const { error } = this.state
    if (error === null) return this.props.children
    if (isSupportSessionBlocked(error)) return <SupportSessionBlockedState />
    throw error
  }
}
